import React, { useEffect } from 'react';
import cn from 'classnames';
import styles from './AudioPlaybackThumbnail.module.css';
import PauseIcon from '../../../SharedComponents/svg/Icons/PauseIcon';
import PlayIcon from '../../../SharedComponents/svg/Icons/PlayIcon';
import LoadingSpinner from '../../../SharedComponents/LoadingSpinner/LoadingSpinner';
import useNonNullContext from '../../../Hooks/useNonNullContext';
import { AudioPlayerContext } from '../AudioPlayerContext';
import { Image } from '@round/ui-kit';

type AudioPlaybackThumbnailProps = {
    loading?: boolean;
    imageUrl?: string;
    audioUrl?: string;
    className?: string;
    alt?: string;
    skeletonCircle?: boolean;
};

const AudioPlaybackThumbnail = ({
    audioUrl,
    imageUrl,
    className,
    alt,
    skeletonCircle,
    loading: isLoading,
}: AudioPlaybackThumbnailProps) => {
    const { playing, togglePlayPause, audio, load, loading: isAudioLoading } = useNonNullContext(AudioPlayerContext);
    const currentSrc = audio?.src;
    const isSelectedAudio = audioUrl && audioUrl === currentSrc;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!audioUrl) {
            return;
        }

        if (currentSrc !== audioUrl) {
            load({
                src: audioUrl,
                autoplay: true,
            });
            return;
        }

        togglePlayPause();
    };

    useEffect(() => {
        return () => {
            audio?.pause();
        };
    }, [audio]);

    const renderOverlay = () => {
        if (isSelectedAudio && isAudioLoading) {
            return <LoadingSpinner containerClassName={styles.spinner} />;
        }

        return isSelectedAudio && playing ? <PauseIcon width={14} height={14} /> : <PlayIcon width={14} height={14} />;
    };

    return (
        <div className={cn(styles.container, className, { [styles.clickable]: audioUrl })} onClick={handleClick}>
            <Image
                skeletonCircle={skeletonCircle}
                className={styles.image}
                src={imageUrl}
                alt={alt || 'Audio'}
                loading={isLoading}
            />
            {audioUrl && !isLoading && (
                <div
                    className={cn(styles.overlay, {
                        [styles.hidden]: !isSelectedAudio && (!isAudioLoading || !playing),
                    })}
                >
                    {renderOverlay()}
                </div>
            )}
        </div>
    );
};

export default AudioPlaybackThumbnail;
